import React, { useMemo, useEffect } from 'react';

import useAuth from '../../../../auth/useAuth';

import Header from './header';

import { teamNamesColors } from '../data';
import { getContrastText } from '../celebrations/helpers';
import { roundNames } from './data';

import TeamScore from './teamScore';

import ChampionshipMaster from './images/championship-master.png';

import confetti from 'canvas-confetti';

import './styles.css';

export default function TournamentBracket({ 
  roundCount,
  currentRound,
  gameType,
  rounds = [],
  matchups = [],
  teams = [],
  teamScores = [],
  isScoresUpdated,
  isReviewButtonLoading,
  onReviewButtonClick,
  isReviewButtonDisabled
}) {
  const { isStudent, getUser } = useAuth();
  const user = getUser();
  const studentTeam = isStudent() ? teams.find(team => team.user_ids.includes(user.id)) : null;


  const bracketStructure = useMemo(() => {
    if (!roundCount || roundCount === 0) return null;

    const structure = {
      leftRounds: [],
      rightRounds: [],
      championship: null
    };

    const getMatchupsForRound = (roundNumber) => {
      const round = rounds.find(r => r.number === roundNumber);
      if (!round) return [];
      return matchups
        .filter(m => m.round_id === round.id)
        .sort((a, b) => a.bracket_slot - b.bracket_slot);
    };

    for (let roundIndex = 0; roundIndex < roundCount - 1; roundIndex++) {
      const roundNumber = roundIndex + 1;
      const roundName = roundNames[roundCount]?.[roundIndex] || '';
      const matchupsInRound = Math.pow(2, roundCount - roundIndex - 1);
      
      const roundMatchups = getMatchupsForRound(roundNumber);
      const allMatchups = [];
      
      for (let i = 0; i < matchupsInRound; i++) {
        allMatchups.push(roundMatchups[i] || {
          id: `placeholder-${roundIndex + 1}-${i}`,
          team1_id: null,
          team2_id: null
        });
      }
      
      const halfPoint = matchupsInRound / 2;
      
      structure.leftRounds.push({
        name: roundName,
        roundIndex: roundIndex,
        matchups: allMatchups.slice(0, halfPoint)
      });
      
      structure.rightRounds.push({
        name: roundName,
        roundIndex: roundIndex,
        matchups: allMatchups.slice(halfPoint)
      });
    }

    const championshipRoundIndex = roundCount - 1;
    const championshipRoundNumber = championshipRoundIndex + 1;
    const championshipMatchups = getMatchupsForRound(championshipRoundNumber);
    
    structure.championship = {
      name: roundNames[roundCount]?.[championshipRoundIndex] || '',
      matchup: championshipMatchups[0] || { 
        team1_id: null, 
        team2_id: null 
      }
    };

    structure.rightRounds.reverse();

    return structure;
  }, [roundCount, matchups, rounds]);

  
  useEffect(() => {
    if (isScoresUpdated && currentRound === roundCount && bracketStructure?.championship) {
      const duration = 4 * 1000; 
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 20, spread: 500, ticks: 100, zIndex: 10000 };
      
      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();
        
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        
        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: Math.random() * 0.4, y: Math.random() }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: 0.6 + Math.random() * 0.4, y: Math.random() }
        });
      }, 250);
    }
  }, [isScoresUpdated, currentRound, roundCount, bracketStructure]);

  const getTeamName = (teamId) => {
    if (!teamId) return '';
    const team = teams.find(team => team.id === teamId);
    return team?.name;
  };
  
  const getTeamColor = (teamName) => {
    if (!teamName) return '#A98DF6';
    const team = teamNamesColors.find(team => team.name === teamName);
    return team?.color || '#A98DF6';
  };

  const getTeamScore = (teamId, roundNumber) => {
    if (!teamId) return 0;
    
    const scoreData = teamScores.find(
      teamScore => teamScore.team_id === teamId && teamScore.round_number === roundNumber
    );
    
    if (!scoreData) return 0;
    
    if (roundNumber <= currentRound) {
      // return (scoreData.team_total_points || 0) + (scoreData.team_result_bonus || 0);
      return (scoreData.team_total_points || 0);
    }
    
    return 0;
  };

  const getTeamResult = (teamId, roundNumber) => {
    if (!teamId) return null;
    
    if (roundNumber <= currentRound) {
      const scoreData = teamScores.find(
        teamScore => teamScore.team_id === teamId && teamScore.round_number === roundNumber
      );
      return scoreData?.team_result || null;
    }
    
    return null;
  };
  
  const getTeamSlug = (teamName) => {
    if (!teamName) return null;
    return teamName.toLowerCase().replace(/\s/g, '-');
  };

  const renderMatchups = (matchups, roundNumber, roundName) => {
    const pairs = [];
    for (let i = 0; i < matchups.length; i += 2) {
      const pair = matchups.slice(i, i + 2);
      pairs.push(
        <div key={`pair-${i / 2}`} className={`matchups-pair ${matchups.length === 1 ? 'single-matchup-pair' : ''}`}>
          {pair.map((matchup, index) => {
            const team1Name = getTeamName(matchup.team1_id);
            const team1Color = getTeamColor(team1Name);
            const team1Score = getTeamScore(matchup.team1_id, roundNumber);
            const team1Result = getTeamResult(matchup.team1_id, roundNumber);
            const team1Active = team1Result === 'WINNER' || team1Result === 'TIED';
            const team1StudentActive = studentTeam?.id === matchup.team1_id && roundNumber === currentRound;
            const team1Slug = getTeamSlug(team1Name);

            const team2Name = getTeamName(matchup.team2_id);
            const team2Color = getTeamColor(team2Name);
            const team2Score = getTeamScore(matchup.team2_id, roundNumber);
            const team2Result = getTeamResult(matchup.team2_id, roundNumber);
            const team2Active = team2Result === 'WINNER' || team2Result === 'TIED';
            const team2StudentActive = studentTeam?.id === matchup.team2_id && roundNumber === currentRound;
            const team2Slug = getTeamSlug(team2Name);

            return (
              <React.Fragment key={matchup.id || `${i + index}`}>
                {
                  currentRound >= roundNumber 
                    ? <div key={matchup.id || `${i + index}`} className="matchup">
                        <div className="matchup-box">
                          <div
                            className={`team-slot ${team1Slug}${team1Active ? ' active' : ''}`}
                            style={{
                              backgroundColor: team1Active ? team1Color : 'transparent',
                              ...(team1Active && { color: getContrastText(team1Color) })
                            }}
                          >
                            {
                              team1StudentActive &&
                                <span className="team-student-active" style={{ backgroundColor: team1Color}}></span>
                            }
                            <span className="tournament-team-indicator" style={{backgroundColor: team1Color}}></span>
                            <span className="team-name">{team1Name}</span>
                            <TeamScore score={team1Score} />
                          </div>
                          <div
                            className={`team-slot ${team2Slug}${team2Active ? ' active' : ''}`}
                            style={{
                              backgroundColor: team2Active ? team2Color : 'transparent',
                              ...(team2Active && { color: getContrastText(team2Color) })
                            }}
                          >
                            {
                              team2StudentActive &&
                                <span className="team-student-active" style={{ backgroundColor: team2Color}}></span>
                            }
                            <span className="tournament-team-indicator" style={{backgroundColor: team2Color}}></span>
                            <span className="team-name">{team2Name}</span>
                            <TeamScore score={team2Score} />
                          </div>
                        </div>
                      </div>
                    : <div key={matchup.id || `${i + index}`} className="matchup matchup-placeholder">
                        <div className="matchup-box">
                          <span>{roundName}</span>
                        </div>
                      </div>
                }
                {
                  index === 0 && 
                    <div className="brackets">
                      {
                        matchups.length > 1 &&
                          <>
                            <div className="bracket bracket-top"></div>
                            <div className="bracket bracket-bottom"></div>
                          </>
                      }
                      {
                        matchups.length === 1 &&
                          <>
                            <div className="bracket bracket-middle"></div>
                          </>
                      }
                    </div>
                }
              </React.Fragment>
            )
          })}
        </div>
      );
    }
    return pairs;
  }

  return (
    <div className="tournament-bracket-container" data-tour-id="competition-overview">
      <Header
        roundCount={roundCount}
        gameType={gameType}
        currentRound={currentRound}
        isReviewButtonLoading={isReviewButtonLoading}
        onReviewButtonClick={onReviewButtonClick}
        isReviewButtonDisabled={isReviewButtonDisabled}
      />
      <div className="tournament-bracket-body">
        <div className="bracket-side bracket-left">
          {bracketStructure.leftRounds.map((round, index) => (
              <div key={index} className={`bracket-column column-round-${round.roundIndex + 1}${round.roundIndex + 1 === currentRound ? ' active' : ''}`}>
                <div className="matchups-container">
                  {renderMatchups(round.matchups, index + 1, round.name)}
                </div>
              </div>
            )
          )}
        </div>

        <div className="championship-container">
          <div className="master-badge">
            <img src={ChampionshipMaster} alt="Championship Master"/>
          </div>
          {bracketStructure.championship && (
            <>
              {currentRound === roundCount ? (
                <div className="matchup">
                  <div className="matchup-box championship-box">
                    <div
                      className={`team-slot ${getTeamSlug(getTeamName(bracketStructure.championship.matchup.team1_id))}${
                        getTeamResult(bracketStructure.championship.matchup.team1_id, roundCount) === 'WINNER' ||
                        getTeamResult(bracketStructure.championship.matchup.team1_id, roundCount) === 'TIED' ? ' active' : ''
                      }`}
                      style={(() => {
                        const isActive = getTeamResult(bracketStructure.championship.matchup.team1_id, roundCount) === 'WINNER'
                          || getTeamResult(bracketStructure.championship.matchup.team1_id, roundCount) === 'TIED';
                        const color = getTeamColor(getTeamName(bracketStructure.championship.matchup.team1_id));
                        return {
                          backgroundColor: isActive ? color : 'transparent',
                          ...(isActive && { color: getContrastText(color) })
                        };
                      })()}
                    >
                      {
                        studentTeam?.id === bracketStructure.championship.matchup.team1_id &&
                          <span 
                            className="team-student-active" 
                            style={{ 
                              backgroundColor: getTeamColor(getTeamName(bracketStructure.championship.matchup.team1_id))
                            }}
                          ></span>
                      }
                      <span 
                        className="tournament-team-indicator"
                        style={{
                          backgroundColor: getTeamColor(getTeamName(bracketStructure.championship.matchup.team1_id))
                        }}
                      ></span>
                      <span>{getTeamName(bracketStructure.championship.matchup.team1_id)}</span>
                      <TeamScore score={getTeamScore(bracketStructure.championship.matchup.team1_id, roundCount)} />
                    </div>
                    <div
                      className={`team-slot ${getTeamSlug(getTeamName(bracketStructure.championship.matchup.team2_id))}${
                        getTeamResult(bracketStructure.championship.matchup.team2_id, roundCount) === 'WINNER' ||
                        getTeamResult(bracketStructure.championship.matchup.team2_id, roundCount) === 'TIED' ? ' active' : ''
                      }`}
                      style={(() => {
                        const isActive = getTeamResult(bracketStructure.championship.matchup.team2_id, roundCount) === 'WINNER'
                          || getTeamResult(bracketStructure.championship.matchup.team2_id, roundCount) === 'TIED';
                        const color = getTeamColor(getTeamName(bracketStructure.championship.matchup.team2_id));
                        return {
                          backgroundColor: isActive ? color : 'transparent',
                          ...(isActive && { color: getContrastText(color) })
                        };
                      })()}
                    >
                      {
                        studentTeam?.id === bracketStructure.championship.matchup.team2_id &&
                          <span 
                            className="team-student-active" 
                            style={{ 
                              backgroundColor: getTeamColor(getTeamName(bracketStructure.championship.matchup.team2_id))
                            }}
                          ></span>
                      }
                      <span 
                        className="tournament-team-indicator"
                        style={{
                          backgroundColor: getTeamColor(getTeamName(bracketStructure.championship.matchup.team2_id))
                        }}
                      ></span>
                      <span>{getTeamName(bracketStructure.championship.matchup.team2_id)}</span>
                      <TeamScore score={getTeamScore(bracketStructure.championship.matchup.team2_id, roundCount)} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="matchup matchup-placeholder">
                  <div className="matchup-box">
                    <span>{bracketStructure.championship.name}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bracket-side bracket-right">
          {bracketStructure.rightRounds.map((round, index) => (
            <div key={index} className={`bracket-column column-round-${round.roundIndex + 1}${round.roundIndex + 1 === currentRound ? ' active' : ''}`}>
              <div className="matchups-container">
                {renderMatchups(round.matchups, roundCount - index - 1, round.name)}
              </div>
            </div>
          ))}
        </div>

        
      </div>
    </div>
  );
}